"use client";

import { memo, useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ApiErrorProvider } from "../ReactHookForm/ApiError/ApiError";
import { Center } from "../ui/center";
import { Stack } from "../ui/stack";
import { Text } from "../ui/text";
import { TwoFactorAuthChallengeButtonSendCode } from "./ButtonSendCode";
import { TwoFactorAuthChallengeCountDownCountDown } from "./CountDown";
import { TwoFactorAuthChallengeForm } from "./Form";
import { TwoFactorAuthChallengeHandler } from "./Handler";

export const TwoFactorAuthChallengeModalBody = memo(
  /**
   * @typedef {object} Props
   * @prop {(isSuccess: boolean) => void} resolve
   * @prop {import("../../hooks/useApiFetcher").TwoFactorAuthChallengeData} challengeData
   */
  /**
   * @param {Props} props
   */
  function TwoFactorAuthChallengeModalBody({ resolve, challengeData }) {
    const [codeSent, setCodeSent] = useState(false);

    const handleSuccess = useCallback(() => {
      resolve(true);
    }, [resolve]);

    const handleCodeSent = useCallback(() => {
      setCodeSent(true);
    }, []);

    return (
      <Stack gap="16px">
        {challengeData?.method === "email" && !codeSent && (
          <Text>
            <FormattedMessage
              defaultMessage="Un code vous sera envoyé par email à l’adresse <bold>{email}</bold>."
              values={{
                bold: (chunks) => <strong>{chunks}</strong>,
                email: challengeData.email,
              }}
            />
          </Text>
        )}

        {challengeData?.method === "sms" && !codeSent && (
          <Text>
            <FormattedMessage
              defaultMessage="Un code vous sera envoyé par sms au numéro <bold>{phoneNumber}</bold>."
              values={{
                bold: (chunks) => <strong>{chunks}</strong>,
                phoneNumber: challengeData.phoneNumber,
              }}
            />
          </Text>
        )}

        {(challengeData?.method === "app" || codeSent) && (
          <>
            {challengeData?.method === "app" && (
              <Text>
                <FormattedMessage
                  defaultMessage="Veuillez saisir le code généré par votre application d’authentification."
                  values={{
                    bold: (chunks) => <strong>{chunks}</strong>,
                    email: challengeData.email,
                  }}
                />
              </Text>
            )}

            {challengeData?.method === "email" && (
              <Text>
                <FormattedMessage
                  defaultMessage="Veuillez saisir le code qui vous a été envoyé par email à l’adresse <bold>{email}</bold>."
                  values={{
                    bold: (chunks) => <strong>{chunks}</strong>,
                    email: challengeData.email,
                  }}
                />
              </Text>
            )}

            {challengeData?.method === "sms" && (
              <Text>
                <FormattedMessage
                  defaultMessage="Veuillez saisir le code qui vous a été envoyé par SMS au numéro <bold>{phoneNumber}</bold>."
                  values={{
                    bold: (chunks) => <strong>{chunks}</strong>,
                    phoneNumber: challengeData.phoneNumber,
                  }}
                />
              </Text>
            )}

            <ApiErrorProvider>
              <TwoFactorAuthChallengeHandler
                challengeId={challengeData.challengeId}
                onSuccess={handleSuccess}
              >
                {({ form, submit, isLoading }) => (
                  <form onSubmit={submit}>
                    <TwoFactorAuthChallengeForm
                      form={form}
                      submit={submit}
                      isLoading={isLoading}
                    />
                  </form>
                )}
              </TwoFactorAuthChallengeHandler>
            </ApiErrorProvider>
          </>
        )}

        {(challengeData?.method === "email" ||
          challengeData?.method === "sms") && (
          <Center>
            <TwoFactorAuthChallengeButtonSendCode
              challengeId={challengeData.challengeId}
              onCodeSent={handleCodeSent}
            />
          </Center>
        )}

        {challengeData?.expiresAt !== undefined && (
          <TwoFactorAuthChallengeCountDownCountDown
            expiresAt={challengeData.expiresAt}
          />
        )}
      </Stack>
    );
  },
);
